import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { SUPPORTED_LANGUAGES, type SupportedLanguage } from "../i18n";
import { getLocaleFromPath } from "../lib/locale-utils";

/**
 * BCP-47 locale mapping for OG tags (lang code -> og:locale value).
 */
export const OG_LOCALE_MAP: Record<SupportedLanguage, string> = {
  en: "en_US",
  es: "es_ES",
  ar: "ar_001",
  fr: "fr_FR",
  pt: "pt_BR",
  de: "de_DE",
  nl: "nl_NL",
  hr: "hr_HR",
  fa: "fa_IR",
  cs: "cs_CZ",
  no: "nb_NO",
  sv: "sv_SE",
  ko: "ko_KR",
  ja: "ja_JP",
  tr: "tr_TR",
  uz: "uz_UZ",
};

/**
 * Injects <meta property="og:locale"> and <meta property="og:locale:alternate">
 * tags into <head>. Reads locale from URL path for SSG correctness.
 */
export default function OgLocaleTags() {
  const { i18n } = useTranslation();

  useEffect(() => {
    // Remove existing OG locale tags we manage
    document.querySelectorAll("meta[data-og-locale]").forEach((el) => el.remove());

    // Read locale from URL path (source of truth) rather than i18n.language
    const currentLang = getLocaleFromPath(window.location.pathname);
    const currentLocale = OG_LOCALE_MAP[currentLang] ?? "en_US";

    // Primary og:locale
    const primary = document.createElement("meta");
    primary.setAttribute("property", "og:locale");
    primary.setAttribute("content", currentLocale);
    primary.setAttribute("data-og-locale", "true");
    document.head.appendChild(primary);

    // Alternates for all other languages
    for (const lang of SUPPORTED_LANGUAGES) {
      if (lang === currentLang) continue;
      const alt = document.createElement("meta");
      alt.setAttribute("property", "og:locale:alternate");
      alt.setAttribute("content", OG_LOCALE_MAP[lang]);
      alt.setAttribute("data-og-locale", "true");
      document.head.appendChild(alt);
    }
  }, [i18n.language]);

  return null;
}
